/**
 * Frozen cache identities for `@heidi/templates` (C2, ARCFE-1009).
 *
 * Every string here is the byte-identical literal the app already ships. A
 * relocation that changes a key splits the cache in two — the app writes under
 * the old identity and the package reads the new one — so these are copied
 * verbatim from their measured definition sites and the legacy sites become
 * re-exports, never re-typed literals.
 */

/**
 * `packages/templates/src/data/query/useQuerySharedTemplate.ts` — the shared-template read.
 * Series D: moves with `templates/shared-template`, the first D2 family.
 */
export const GET_SHARED_TEMPLATE = 'getSharedTemplate';

/**
 * `packages/templates/src/data/mutation/useMutateCreateTemplateSuggestions.ts`
 * — a mutationKey, not a query key. It was frozen here ahead of its hook, which
 * has since moved in behind the data door and imports this identity; the old app
 * path re-exports it from this door rather than re-typing the literal.
 */
export const TEMPLATE_SUGGESTIONS_MUTATION_KEY = ['template-suggestions'] as const;

// ---------------------------------------------------------------------------
// Template list/detail identities (D2, ARCFE-1016)
//
// Moved because the write hooks invalidate them: a mutation cannot carve while
// the keys it invalidates live in app hooks. Byte-identical to what the app
// ships — note the inconsistent casing (`Get_templates` vs `GetDocumentTemplates`
// vs `getOrganizationTemplates`). It is preserved exactly; normalising the
// spelling would silently repartition every one of these caches, and it would
// look like a tidy-up.
// ---------------------------------------------------------------------------

export const GET_TEMPLATES = `Get_templates`;

export const GET_DOCUMENT_TEMPLATES = `GetDocumentTemplates`;

export const GET_CONSULT_NOTE_TEMPLATES = `GetConsultNoteTemplates`;

export const GET_PINNED_TEMPLATES = `GetPinnedTemplates`;

export const GET_TEMPLATE_BY_ID = 'GetTemplateById';

export const GET_ORGANIZATION_TEMPLATES = 'getOrganizationTemplates';

export const GET_ORGANIZATION_TEMPLATES_FILTERS_METADATA =
  'getOrganizationTemplatesFiltersMetadata';

export const GET_DICTATION_TEMPLATES = `Get_dictation_templates`;

/** Template-gallery like identities (S2, ARCFE-1017). */
export const COMMUNITY_TEMPLATE_LIKE_STATUS_KEY = 'COMMUNITY_TEMPLATE_LIKE_STATUS';
export const COMMUNITY_TEMPLATE_BATCH_LIKE_STATUS_KEY =
  'COMMUNITY_TEMPLATE_BATCH_LIKE_STATUS';

/** The public gallery's detail read (D2.2, ARCFE-1026). */
export const COMMUNITY_TEMPLATE_BY_TEMPLATE_ID = 'GET_COMMUNITY_TEMPLATE_BY_TEMPLATE_ID';

/** Gallery form-template read + the initial pinned list (D2.2, ARCFE-1027). */
export const GET_FORM_TEMPLATE_BY_URL_PATH = 'GET_FORM_TEMPLATE_BY_URL_PATH';
export const GET_INITIAL_PINNED_TEMPLATES = 'GetInitialPinnedTemplates';

/**
 * The community list identity (D2.3, ARCFE-1028). Copied from the source, not
 * inferred: the literal is the SCREAMING form `'GET_COMMUNITY_TEMPLATES'`, not
 * the `GetX` casing its siblings use. Guessing it would have split the cache.
 */
export const GET_COMMUNITY_TEMPLATES = 'GET_COMMUNITY_TEMPLATES';

/**
 * The gallery's FORM-category list (D2.7, ARCFE-1046). Same SCREAMING spelling
 * as its `GET_COMMUNITY_TEMPLATES` sibling, and copied from the definition site
 * rather than derived from it — the two hooks read the same endpoint under two
 * different cache identities, which is deliberate and must survive the move.
 */
export const GET_FORM_TEMPLATES = 'GET_FORM_TEMPLATES';

/**
 * Public community XML form-fill demo. Separate from the PDF demo mutation so
 * the two fills do not share a slot.
 */
export const DEMO_FILL_XML_FORM_MUTATION_KEY = ['demo-fill-xml-form'] as const;

export const DEMO_FILL_XML_FORM_FROM_UPLOAD_MUTATION_KEY = [
  'demo-fill-xml-form-from-upload',
] as const;

export const DEMO_FILL_JSON_FORM_MUTATION_KEY = ['demo-fill-json-form'] as const;

export const DEMO_FILL_JSON_FORM_FROM_UPLOAD_MUTATION_KEY = [
  'demo-fill-json-form-from-upload',
] as const;

/**
 * PDF form-fill demo. A separate slot from the JSON and XML fills, and from
 * the upload variant of this same fill.
 */
export const DEMO_FILL_FORM_MUTATION_KEY = ['demo-fill-form'] as const;

export const DEMO_FILL_FORM_FROM_UPLOAD_MUTATION_KEY = [
  'demo-fill-form-from-upload',
] as const;

/**
 * Filled-PDF cache identities. The url key is the tuple `filledPDF` plus the
 * document id; the fields key spells `GetFilledPdfFields`, not that tuple.
 */
export const GET_FILLED_PDF_FIELDS = 'GetFilledPdfFields';

export const getFilledPdfFieldsQueryKey = (documentId: string) => [
  GET_FILLED_PDF_FIELDS,
  documentId,
];

export const getFilledPDFQueryKey = (documentId: string) => ['filledPDF', documentId];

/**
 * Specialty-recommended template list for the first-note modal.
 * Keyed with specialty/country/language because the backend returns a
 * different curated set per specialty and market.
 */
export const GET_RECOMMENDED_TEMPLATES = 'get_recommended_templates';

/** Creation record looked up by saved template id (library preview and Share). */
export const GET_TEMPLATE_CREATION_RECORD_BY_TEMPLATE_ID =
  'GET_TEMPLATE_CREATION_RECORD_BY_TEMPLATE_ID';

/**
 * User-scoped localStorage identity for the one-time “use sections”
 * intro toast in template builder. Not a React Query cache key — same
 * freeze: the literal lives here so the app atom cannot re-declare it.
 */
export const HAS_SEEN_USE_SECTIONS_INTRO_STORAGE_KEY = 'heidi.hasSeenUseSectionsIntro';

// ---------------------------------------------------------------------------
// templateBuilder cache identities (C2b)
//
// The 11 identities `src/features/templateBuilder` declared, frozen here so the
// hooks that use them can carve. No hook body moved with this block: each
// legacy site now imports its identity from this door and re-exports it where
// it was already exported, so every importer and every `jest.mock` literal
// keeps resolving unchanged.
//
// Each literal is COPIED VERBATIM from its declaration site. Four spellings
// disagree with each other on purpose and all four are preserved:
//
//  - ten are `PascalCase` verb-phrases (`'CreateTemplateStreaming'`) while
//    `GET_TEMPLATE_CREATION_RECORD_BY_ID` is SCREAMING_SNAKE and is the only
//    one whose value equals its constant name;
//  - `GENERATE_EXAMPLE_NOTE_STREAMING_KEY` reads `'GenerateExampleNoteStreaming'`
//    — the constant says NOTE, the value says Note, and neither says `Example`
//    twice, so it cannot be derived from the constant name;
//  - the four `*_STREAMING_KEY` names end in `Streaming` but
//    `CREATE_TEMPLATE_STREAMING_KEY` reads `'CreateTemplateStreaming'`, not
//    `'CreateTemplateStructureStreaming'`, though its hook generates a
//    structure;
//  - `CREATE_LATEST_VERSION_CONTROL_KEY` reads `'CreateLatestVersionControl'`
//    while its sibling `UPDATE_VERSION_CONTROL_KEY` drops nothing and reads
//    `'UpdateVersionControl'`.
//
// Normalising any of them would repartition a live cache and would read as a
// tidy-up in review.
// ---------------------------------------------------------------------------

/**
 * The template-creation-record read. Ten of the eleven hooks below write into
 * or invalidate THIS cache, which is why the set moves together.
 */
export const GET_TEMPLATE_CREATION_RECORD_BY_ID = 'GET_TEMPLATE_CREATION_RECORD_BY_ID';

export const CREATE_TEMPLATE_CREATION_RECORD_KEY = 'CreateTemplateCreationRecord';

export const CREATE_TEMPLATE_CREATION_RECORD_BY_TEMPLATE_ID_KEY =
  'CreateTemplateCreationRecordByTemplateId';

export const UPDATE_TEMPLATE_CREATION_RECORD_KEY = 'UpdateTemplateCreationRecord';

export const CREATE_LATEST_VERSION_CONTROL_KEY = 'CreateLatestVersionControl';

export const UPDATE_VERSION_CONTROL_KEY = 'UpdateVersionControl';

/** The four streaming generations, plus the initial one. */
export const CREATE_TEMPLATE_STREAMING_KEY = 'CreateTemplateStreaming';

export const GENERATE_EXAMPLE_NOTE_STREAMING_KEY = 'GenerateExampleNoteStreaming';

export const GENERATE_EXAMPLE_VIA_CHAT_STREAMING_KEY = 'GenerateExampleViaChatStreaming';

export const GENERATE_STRUCTURE_FROM_EXAMPLE_NOTE_STREAMING_KEY =
  'GenerateStructureFromExampleNoteStreaming';

export const GENERATE_STRUCTURE_VIA_CHAT_STREAMING_KEY =
  'GenerateStructureViaChatStreaming';
